/**
 * JSON-LD schema factories for SEO / GEO.
 * Return plain objects — inject via <Seo jsonLd={[...]}/>.
 */

const SITE = "https://orphionglobalhr.com";

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Orphion Global HR",
  alternateName: "Orphion",
  legalName: "Nivyan Business Solutions LLP",
  url: SITE,
  logo: `${SITE}/logo512.png`,
  description:
    "Boutique recruitment and fractional HR practice for IT and non-IT companies.",
  areaServed: [
    "IN", "US", "GB", "CA", "PL", "ES", "UA",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: `${SITE}/contact`,
      availableLanguage: ["en"],
    },
  ],
  sameAs: [],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: SITE,
  name: "Orphion Global HR",
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE}/jobs?search={query}`,
    "query-input": "required name=query",
  },
});

export const professionalServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Orphion Global HR",
  serviceType: [
    "Recruitment",
    "HR Consulting",
    "HR Operations",
    "Employer Branding",
    "GCC Support",
  ],
  areaServed: "Global",
  provider: {
    "@type": "Organization",
    name: "Nivyan Business Solutions LLP",
  },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    addressCountry: "IN",
  },
});

/**
 * A single JobPosting. Pass the full job object from /api/jobs.
 */
export const jobPostingSchema = (job) => {
  const salary =
    job.salary_min || job.salary_max
      ? {
          "@type": "MonetaryAmount",
          currency: "INR",
          value: {
            "@type": "QuantitativeValue",
            minValue: job.salary_min || undefined,
            maxValue: job.salary_max || undefined,
            unitText: "YEAR",
          },
        }
      : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: job.posted_at,
    validThrough: undefined,
    employmentType: (job.employment_type || "FULL_TIME").toUpperCase().replace(/[ -]/g, "_"),
    industry: job.industry,
    hiringOrganization: {
      "@type": "Organization",
      name: job.company,
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: job.location,
        addressCountry: "IN",
      },
    },
    jobLocationType:
      (job.remote_type || "").toLowerCase().includes("remote") ? "TELECOMMUTE" : undefined,
    baseSalary: salary,
    skills: (job.tech_stack || []).join(", "),
    experienceRequirements: job.seniority,
    identifier: {
      "@type": "PropertyValue",
      name: "Orphion",
      value: job.id,
    },
    url: `${SITE}/jobs#${job.id}`,
  };
};

export const jobItemListSchema = (jobs) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: (jobs || []).slice(0, 20).map((j, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: jobPostingSchema(j),
  })),
});

/**
 * FAQPage schema — pass [{q, a}, ...]
 */
export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (items || []).map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: {
      "@type": "Answer",
      text: typeof a === "string" ? a : String(a),
    },
  })),
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: (crumbs || []).map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE}${c.path}`,
  })),
});

/**
 * Article / BlogPosting schema for Insights posts.
 */
export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.excerpt,
  image: post.cover_image ? [post.cover_image] : undefined,
  datePublished: post.published_at,
  dateModified: post.published_at,
  author: { "@type": "Organization", name: post.author || "Orphion Global HR" },
  publisher: {
    "@type": "Organization",
    name: "Orphion Global HR",
    logo: { "@type": "ImageObject", url: `${SITE}/logo512.png` },
  },
  mainEntityOfPage: `${SITE}/insights/${post.slug}`,
  keywords: (post.tags || []).join(", "),
});
